import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_ATTEMPTS,
  deleteIdeaFromOutbox,
  deleteInsightFromOutbox,
  getIdeaOutboxItem,
  getInsightOutboxItem,
  listIdeaOutbox,
  listInsightOutbox,
  subscribeEcwOutboxChanges,
  updateIdeaOutboxItem,
  updateInsightOutboxItem,
  type OutboxIdea,
  type OutboxInsight,
} from './ecwOutbox';
import { sendInsightOutboxItem } from './useEcwInsights';
import { sendIdeaOutboxItem } from './useEcwIdeas';

/**
 * 気づき (insight) + 処方箋 (idea) 用 outbox hook (POC、posts/useOutbox.ts と同パターン)。
 *
 * 提供する state:
 *   - `count`: insight + idea 合算 未送信件数 (header badge 用)
 *   - `pendingCount` / `failedCount`: status 別件数
 *   - `insights` / `ideas`: 個別 list (将来 UI section 化用)
 *   - `isFlushing`: 自動 / 手動 flush 実行中
 *
 * 自動 flush:
 *   - online 復帰時 (skipFailed:true、posts/useOutbox と同様)
 *   - 初回 mount でも online なら自動 flush
 *   - 他 tab で outbox 変化したら refresh (BroadcastChannel)
 *
 * insight / idea を 1 つの hook に統合した理由:
 *   - header badge は合算件数で表示するため、UI 側で 2 hook を統合するより内部で纏める方が単純
 *   - flush は両 store を順次処理 (insight → idea の順、依存関係は無いが順序固定で test 容易)
 */

export interface EcwFlushSummary {
  sent: number;
  failed: number;
  skipped: number;
}

export interface EcwOutboxState {
  /** insights + ideas の合算件数。 */
  count: number;
  /** pending: status 未設定 or 'pending' な item の件数。 */
  pendingCount: number;
  /** failed: status='failed' (MAX_ATTEMPTS 超過) の件数。 */
  failedCount: number;
  insights: OutboxInsight[];
  ideas: OutboxIdea[];
  isFlushing: boolean;
  /**
   * 直近 flush の結果サマリ (自動 / 手動どちらでも更新)。
   * Task B: AppHome 側で useEffect で監視し、failed > 0 のとき toast 表示する。
   * 未 flush 時 (mount 直後で skip された場合含む) は null。
   */
  lastFlushResult: EcwFlushSummary | null;
}

export interface EcwFlushOptions {
  skipFailed?: boolean;
}

export interface RetryOneResult {
  ok: boolean;
  notFound?: boolean;
  lastError?: string;
}

function classify(insights: OutboxInsight[], ideas: OutboxIdea[]) {
  let pendingCount = 0;
  let failedCount = 0;
  for (const item of insights) {
    if (item.status === 'failed') failedCount += 1;
    else pendingCount += 1;
  }
  for (const item of ideas) {
    if (item.status === 'failed') failedCount += 1;
    else pendingCount += 1;
  }
  return { pendingCount, failedCount };
}

/**
 * insight 失敗時の attempts/status 更新 helper (posts/useOutbox.markItemFailedOnce 踏襲)。
 */
async function markInsightFailedOnce(item: OutboxInsight, error: unknown): Promise<string> {
  const lastError = error instanceof Error ? error.message : String(error);
  if (item.id === undefined) return lastError;
  const nextAttempts = item.attempts + 1;
  const nextStatus: OutboxInsight['status'] = nextAttempts >= MAX_ATTEMPTS ? 'failed' : 'pending';
  await updateInsightOutboxItem({
    ...item,
    lastError,
    attempts: nextAttempts,
    status: nextStatus,
  });
  return lastError;
}

async function markIdeaFailedOnce(item: OutboxIdea, error: unknown): Promise<string> {
  const lastError = error instanceof Error ? error.message : String(error);
  if (item.id === undefined) return lastError;
  const nextAttempts = item.attempts + 1;
  const nextStatus: OutboxIdea['status'] = nextAttempts >= MAX_ATTEMPTS ? 'failed' : 'pending';
  await updateIdeaOutboxItem({
    ...item,
    lastError,
    attempts: nextAttempts,
    status: nextStatus,
  });
  return lastError;
}

export function useEcwOutbox(): {
  state: EcwOutboxState;
  flush: (options?: EcwFlushOptions) => Promise<{ sent: number; failed: number; skipped: number }>;
  retryInsight: (id: number) => Promise<RetryOneResult>;
  retryIdea: (id: number) => Promise<RetryOneResult>;
  refresh: () => Promise<void>;
} {
  const [state, setState] = useState<EcwOutboxState>({
    count: 0,
    pendingCount: 0,
    failedCount: 0,
    insights: [],
    ideas: [],
    isFlushing: false,
    lastFlushResult: null,
  });

  const isMountedRef = useRef(true);

  const refresh = useCallback(async () => {
    const [insights, ideas] = await Promise.all([listInsightOutbox(), listIdeaOutbox()]);
    if (!isMountedRef.current) return;
    const counts = classify(insights, ideas);
    setState((s) => ({
      ...s,
      insights,
      ideas,
      count: insights.length + ideas.length,
      ...counts,
    }));
  }, []);

  const flush = useCallback(async (options?: EcwFlushOptions) => {
    if (isMountedRef.current) {
      setState((s) => ({ ...s, isFlushing: true }));
    }
    let sent = 0;
    let failed = 0;
    let skipped = 0;
    try {
      const [allInsights, allIdeas] = await Promise.all([listInsightOutbox(), listIdeaOutbox()]);
      const insights = options?.skipFailed
        ? allInsights.filter((item) => {
            if (item.status === 'failed') {
              skipped += 1;
              return false;
            }
            return true;
          })
        : allInsights;
      const ideas = options?.skipFailed
        ? allIdeas.filter((item) => {
            if (item.status === 'failed') {
              skipped += 1;
              return false;
            }
            return true;
          })
        : allIdeas;
      for (const item of insights) {
        try {
          await sendInsightOutboxItem(item);
          if (item.id !== undefined) await deleteInsightFromOutbox(item.id);
          sent += 1;
        } catch (e) {
          failed += 1;
          await markInsightFailedOnce(item, e);
        }
      }
      for (const item of ideas) {
        try {
          await sendIdeaOutboxItem(item);
          if (item.id !== undefined) await deleteIdeaFromOutbox(item.id);
          sent += 1;
        } catch (e) {
          failed += 1;
          await markIdeaFailedOnce(item, e);
        }
      }
    } finally {
      const [remainingInsights, remainingIdeas] = await Promise.all([
        listInsightOutbox(),
        listIdeaOutbox(),
      ]);
      if (isMountedRef.current) {
        const counts = classify(remainingInsights, remainingIdeas);
        setState({
          count: remainingInsights.length + remainingIdeas.length,
          insights: remainingInsights,
          ideas: remainingIdeas,
          isFlushing: false,
          lastFlushResult: { sent, failed, skipped },
          ...counts,
        });
      }
    }
    return { sent, failed, skipped };
  }, []);

  const retryInsight = useCallback(
    async (id: number): Promise<RetryOneResult> => {
      const item = await getInsightOutboxItem(id);
      if (!item) {
        await refresh();
        return { ok: false, notFound: true };
      }
      if (isMountedRef.current) {
        setState((s) => ({ ...s, isFlushing: true }));
      }
      try {
        await sendInsightOutboxItem(item);
        if (item.id !== undefined) await deleteInsightFromOutbox(item.id);
        await refresh();
        return { ok: true };
      } catch (e) {
        const lastError = await markInsightFailedOnce(item, e);
        await refresh();
        return { ok: false, lastError };
      } finally {
        if (isMountedRef.current) {
          setState((s) => ({ ...s, isFlushing: false }));
        }
      }
    },
    [refresh],
  );

  const retryIdea = useCallback(
    async (id: number): Promise<RetryOneResult> => {
      const item = await getIdeaOutboxItem(id);
      if (!item) {
        await refresh();
        return { ok: false, notFound: true };
      }
      if (isMountedRef.current) {
        setState((s) => ({ ...s, isFlushing: true }));
      }
      try {
        await sendIdeaOutboxItem(item);
        if (item.id !== undefined) await deleteIdeaFromOutbox(item.id);
        await refresh();
        return { ok: true };
      } catch (e) {
        const lastError = await markIdeaFailedOnce(item, e);
        await refresh();
        return { ok: false, lastError };
      } finally {
        if (isMountedRef.current) {
          setState((s) => ({ ...s, isFlushing: false }));
        }
      }
    },
    [refresh],
  );

  useEffect(() => {
    isMountedRef.current = true;
    let cancelled = false;
    void (async () => {
      const [insights, ideas] = await Promise.all([listInsightOutbox(), listIdeaOutbox()]);
      if (cancelled || !isMountedRef.current) return;
      const counts = classify(insights, ideas);
      setState({
        count: insights.length + ideas.length,
        insights,
        ideas,
        isFlushing: false,
        lastFlushResult: null,
        ...counts,
      });
      if (insights.length + ideas.length > 0 && navigator.onLine) {
        void flush({ skipFailed: true });
      }
    })();

    function onOnline() {
      void (async () => {
        const [insights, ideas] = await Promise.all([listInsightOutbox(), listIdeaOutbox()]);
        if (insights.length + ideas.length > 0) void flush({ skipFailed: true });
      })();
    }
    window.addEventListener('online', onOnline);
    const unsubscribe = subscribeEcwOutboxChanges(() => {
      void refresh();
    });
    return () => {
      cancelled = true;
      isMountedRef.current = false;
      window.removeEventListener('online', onOnline);
      unsubscribe();
    };
  }, [flush, refresh]);

  return { state, flush, retryInsight, retryIdea, refresh };
}
